// frontend/src/lib/Can.tsx
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useCan, firstAllowedRoute } from './rbac';

/** Renderiza children solo si el usuario tiene `cap` (o ALGUNA de `any`). */
export function Can({
  cap,
  any,
  fallback = null,
  children,
}: {
  cap?: string;
  any?: string[];
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const can = useCan();
  const ok = cap ? can(cap) : any ? any.some((c) => can(c)) : false;
  return <>{ok ? children : fallback}</>;
}

export function NoAccess() {
  const can = useCan();
  // El inicio puede estar vedado: mandamos a la primera pantalla que sí tenga permitida.
  const to = firstAllowedRoute(can) ?? '/';
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-2">
      <p className="text-lg font-semibold text-[var(--color-text)]">No tenés acceso a esta sección</p>
      <p className="text-sm text-[var(--color-text-muted)]">
        Si creés que deberías verla, pedile al administrador que revise tu rol.
      </p>
      <Link to={to} className="text-sm text-[var(--color-primary)] underline">
        {to === '/' ? 'Volver al inicio' : 'Ir a la primera pantalla disponible'}
      </Link>
    </div>
  );
}

/** Guardia de ruta: muestra la pantalla o el aviso de sin acceso.
 *  Acepta `cap` (una capacidad) o `any` (entra con CUALQUIERA de la lista).
 *  (Decisión: mensaje en lugar de redirección, así el usuario entiende qué pasó.) */
export function RequireCap({ cap, any, children }: { cap?: string; any?: string[]; children: ReactNode }) {
  const can = useCan();
  const ok = cap ? can(cap) : any ? any.some((c) => can(c)) : false;
  return <>{ok ? children : <NoAccess />}</>;
}
